import { IconButton, Paper, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import React from 'react';
import Colors from '../../utils/color';

/**
 * ConsoleCard — the shared HITL 3.0 console card frame.
 *
 * One reusable shell for every console card (Customer Pulse / Live Transcript /
 * Action Center on the left, Agent Cart / POS Cart on the right): a flush white
 * section with an inline header (outlined icon + uppercase title + optional right
 * slot + accordion chevron) and a collapsible body — no per-card border/radius
 * and no divider under the heading. Previously each card re-implemented this
 * skeleton (identical `card`/`header`/`title`/`chevIcon` style blocks); this
 * collapses them onto one contract so the fonts, spacing and collapse behavior
 * stay consistent by construction.
 *
 * Collapse is controlled by the parent (open + onToggle) so the resizable panel
 * and the chevron stay in sync (see useLeftStackCollapse / useRightStackCollapse).
 *
 * Slots:
 *   headerRight  — node between the title and the chevron (e.g. the cart's item count)
 *   headerAction — REPLACES the chevron entirely (e.g. the transcript takeover Close button)
 *   footer       — always-visible content below the body (e.g. Pulse's AI status band);
 *                  renders in both open and collapsed states
 *   children     — the collapsible body; shown only when `open`
 */

const useStyles = makeStyles((theme: Theme) => ({
  // Flush HITL 3.0 sections: no per-card border/radius. Cards sit back-to-back
  // inside a pane and are separated only by the pane's hairline resize dividers,
  // so the card itself is a plain white column that fills its panel.
  card: {
    flex: 1,
    minHeight: 0,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: Colors.white,
    overflow: 'hidden',
  },
  // position:relative so a headerAction can absolutely-position itself against
  // the card corner (the transcript takeover Close button).
  header: {
    position: 'relative',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  headerIcon: {
    color: Colors.readBack,
    fontSize: '0.88rem',
    flexShrink: 0,
  },
  // Slim header: smaller uppercase title (was 0.8rem) so the header row costs
  // less vertical space; the content below is what the agent reads.
  title: {
    flex: 1,
    minWidth: 0,
    color: Colors.readBack,
    fontWeight: 700,
    fontSize: '0.66rem',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
  },
  chevIcon: {
    fontSize: '1.1rem',
    transition: 'transform .15s ease',
  },
  // Expanded: point the arrow up (click-to-collapse), matching the mockup's "^".
  chevIconOpen: {
    transform: 'rotate(180deg)',
  },
  // keepBodyMounted wrapper: the body stays in the DOM across collapse and is
  // hidden via CSS (not unmounted). Used for heavy bodies (Agent Cart's
  // OrderReview) whose re-mount is slow and whose effects must keep running
  // while collapsed.
  bodyMounted: {
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  bodyMountedHidden: {
    display: 'none',
  },
}));

export interface ConsoleCardProps {
  /** Outlined MUI icon element; cloned to receive the shared header-icon style. */
  icon: React.ReactElement;
  /** Header label; rendered uppercase via the shared title style. */
  title: string;
  open: boolean;
  onToggle: () => void;
  /** Node rendered between the title and the chevron (e.g. item count). */
  headerRight?: React.ReactNode;
  /** Replaces the chevron entirely (e.g. the transcript takeover Close button). */
  headerAction?: React.ReactNode;
  /** Always-visible content below the body (e.g. Pulse's AI status band). */
  footer?: React.ReactNode;
  /**
   * Keep the body mounted when collapsed (hidden via CSS) instead of
   * unmounting it. Use for heavy bodies whose re-mount is slow / whose effects
   * must keep running while collapsed (e.g. Agent Cart's OrderReview). Default
   * false — light bodies just unmount.
   */
  keepBodyMounted?: boolean;
  /** Collapsible body; shown only when open (or hidden if keepBodyMounted). */
  children?: React.ReactNode;
  testId?: string;
  /** Extra class(es) merged onto the card root. */
  className?: string;
  /**
   * Bottom / horizontal card padding in theme.spacing multiples. The top
   * padding is pinned to 0 (flush layout) so the header hugs the top of the
   * section; `py` sets only the bottom inset.
   */
  py?: number;
  px?: number;
}

const ConsoleCard = ({
  icon,
  title,
  open,
  onToggle,
  headerRight,
  headerAction,
  footer,
  keepBodyMounted = false,
  children,
  testId,
  className,
  py = 1,
  px = 1.25,
}: ConsoleCardProps) => {
  const classes = useStyles();

  const styledIcon = React.isValidElement(icon)
    ? React.cloneElement(icon as React.ReactElement<{ className?: string }>, {
        className: [
          classes.headerIcon,
          (icon.props as { className?: string }).className,
        ]
          .filter(Boolean)
          .join(' '),
      })
    : icon;

  return (
    <Paper
      className={`${classes.card}${className ? ` ${className}` : ''}`}
      elevation={0}
      data-testid={testId}
      // Top padding pinned to 0 so every card's header sits flush against the
      // top of its section (py controls only the bottom inset now).
      sx={{ pt: 0, pb: py, px }}
    >
      <div className={classes.header}>
        {styledIcon}
        <span className={classes.title}>{title}</span>
        {headerRight}
        {headerAction ?? (
          <IconButton
            size="small"
            aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
            onClick={onToggle}
            // sx (not makeStyles) so ml:auto/padding beat MUI ButtonBase's own
            // emotion styles — this right-anchors the chevron to the card corner.
            sx={{ ml: 'auto', mr: -0.5, p: 0.25, color: '#79808a' }}
          >
            <KeyboardArrowDownIcon
              className={`${classes.chevIcon} ${
                open ? classes.chevIconOpen : ''
              }`}
            />
          </IconButton>
        )}
      </div>

      {keepBodyMounted
        ? children != null && (
            <div
              className={open ? classes.bodyMounted : classes.bodyMountedHidden}
            >
              {children}
            </div>
          )
        : open && children}
      {footer}
    </Paper>
  );
};

export default ConsoleCard;
